import React, { useContext, useEffect, useState, useRef } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';

import CustomHeader from '../CustomHeader';
import { useToast } from 'context/ToastContext';
import TextFileViewer from '../PreviewModal/TextFileViewer';
import PDFViewer from '../PreviewModal/PDFViewer';
import DocumentViewer from '../PreviewModal/DocxViewer';
import DocxViewer from '../PreviewModal/DocxViewer';

import { getFileExtension, getFileType, uploadFiles } from 'utils/filesUtils';
import { useForm } from 'react-hook-form';
import CourtEventForm from './CourtEventForm';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import { addEvent } from 'services/Calender/calendarServices';
import { toastConstant } from 'constants/toastmessage';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import moment from 'moment-timezone';
import { getTenantDetails, parseRemiderPayload } from 'utils/utils';
import { useLoaderDispatch } from 'context/LoaderContext';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';

function CreateCourtEventModal() {
  const { modals, selectedItem, isCaseFileManager, isLeadFileManager, caseDetails, hideCreateEventModal, courtDateMsg, onMountUnMount } =
    useFileManagerContext();
  const { subscribe } = useWebSocket();
  const userContext = useContext(UserDetailsContext);
  const { showCreateEventModal, setShowCreateEventModal } = modals;
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [courtEventExtractedData, setCourtEventExtractedData] = useState([]);
  const [isLoading, setIsLoading] = useState([]);
  const { addToast } = useToast();
  const { addActivity } = useActivityLogPreview();

  // Function to handle closing the modal and resetting state
  const handleCloseModal = () => {
    setShowCreateEventModal(false);
    setIsLoading(false);
    setCourtEventExtractedData([]);
  };

  const renderingData = { ...selectedItem, type: findType(selectedItem) };

  const userId = userContext?.firmDetails?.current_user;
  const setLoader = useLoaderDispatch();
  const {
    handleSubmit,
    control,
    resetField,
    setValue,
    getValues,
    watch,
    formState: { errors },
    trigger,
  } = useForm();

  function findType(item) {
    return getFileType(getFileExtension(item?.display_name || item?.file_name));
  }

  const saveCreateEvent = async (data) => {
    if (data?.attachments?.length > 0) {
      setLoader(true);
      await uploadFiles(data?.attachments, 'calendar', selectedItem?.case_id, userId)
        .then((res) => {
          setLoader(false);
          data.attachments = res;
        })
        .catch((err) => {
          setLoader(false);
          console.log(err);
          return;
        });
    }

    data.reminder = parseRemiderPayload(data.reminder);
    data.reminder = Array.from(new Set(data?.reminder));
    data.location = data?.location?.value || null;

    const timezone = userContext?.userDetails?.timezone || moment.tz.guess();

    // Combine the date from 'when' with the times from the time pickers
    // Use moment.tz to interpret the values as being in the user's timezone
    const eventDate = new Date(data.when);

    if (data?.meeting_start_time) {
      const startTime = new Date(data?.meeting_start_time);
      // Create moment in user's timezone with the exact date and time values selected
      data.meeting_start_time = moment
        .tz(
          {
            year: eventDate.getFullYear(),
            month: eventDate.getMonth(),
            date: eventDate.getDate(),
            hour: startTime.getHours(),
            minute: startTime.getMinutes(),
            second: 0,
          },
          timezone
        )
        .format();
    }

    if (data?.meeting_end_time) {
      const endTime = new Date(data.meeting_end_time);
      // Create moment in user's timezone with the exact date and time values selected
      data.meeting_end_time = moment
        .tz(
          {
            year: eventDate.getFullYear(),
            month: eventDate.getMonth(),
            date: eventDate.getDate(),
            hour: endTime.getHours(),
            minute: endTime.getMinutes(),
            second: 0,
          },
          timezone
        )
        .format();
    }

    // For 'when', interpret the selected date as being in the user's timezone
    data.when = moment
      .tz(
        {
          year: eventDate.getFullYear(),
          month: eventDate.getMonth(),
          date: eventDate.getDate(),
          hour: 0,
          minute: 0,
          second: 0,
        },
        timezone
      )
      .format();

    data.case_name = isCaseFileManager || isLeadFileManager ? caseDetails?.case_description?.case_name : data.case?.case_name ?? '';
    data.case_id = isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : selectedItem?.case_id;
    data.is_lead = isLeadFileManager ? isLeadFileManager : data?.case?.is_lead;

    data.icon = data.event_type?.event_type_icon ?? '';

    if (isCaseFileManager || isLeadFileManager) {
      if (caseDetails?.case?.practice_area) {
        data.practice_area = Array.isArray(caseDetails.case.practice_area)
          ? caseDetails.case.practice_area
          : [caseDetails.case.practice_area];
      } else if (caseDetails?.case_description?.case_practice_area) {
        data.practice_area = Array.isArray(caseDetails.case_description.case_practice_area)
          ? caseDetails.case_description.case_practice_area
          : [caseDetails.case_description.case_practice_area];
      } else if (data?.case?.practice_area) {
        data.practice_area = Array.isArray(data.case.practice_area) ? data.case.practice_area : [data.case.practice_area];
      } else if (data?.practice_area) {
        data.practice_area = Array.isArray(data.practice_area) ? data.practice_area : [data.practice_area];
      }
    } else if (!isCaseFileManager && !isLeadFileManager) {
      if (data?.case?.practice_area) {
        data.practice_area = Array.isArray(data.case.practice_area) ? data.case.practice_area : [data.case.practice_area];
      } else if (data?.practice_area) {
        data.practice_area = Array.isArray(data.practice_area) ? data.practice_area : [data.practice_area];
      }
    } else {
      data.practice_area = [];
    }

    data.guests = [...data.contacts, ...data.firm_users];

    if (isCaseFileManager || isLeadFileManager) {
      data.case = {
        id: caseDetails?.case_id,
        value: caseDetails?.case_description?.case_name,
        label:
          caseDetails?.case_description && caseDetails?.case_description.case_name
            ? `${caseDetails.unique_number ? `${caseDetails.unique_number} ` : ''}${caseDetails.case_description.case_name}`
            : 'TBD',
      };
    }

    data.recurrence_keys = data.recurrence_keys ?? {};
    if (data.is_recurring) {
      data.recurrence_keys.recurrence_limit = convertToTimezoneFormat(
        data.recurrence_keys.recurrence_limit,
        userContext?.userDetails?.timezone
      );
    }

    addEvent({
      data,
      case_id: isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : selectedItem?.case_id,
      is_lead: isLeadFileManager ? isLeadFileManager : selectedItem?.is_lead,
    })
      .then((res) => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EVENT_CREATED_SUCCESS);
        setUploadedAttachments();
        setCourtEventExtractedData();
        hideCreateEventModal();
      })
      .catch((err) => {
        console.log(err);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.EVENT_CREATE_FAILURE);
      });
  };

  useEffect(() => {
    const handler = (data) => {
      setIsLoading(false);
      const courtDateMessage = data?.body?.court_date_message;

      setCourtEventExtractedData(courtDateMessage);
      if (courtDateMessage?.isError && courtDateMessage?.message) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, courtDateMessage?.message);
      }
    };
    const unsubscribe = subscribe('ai_court_date_message', handler);
    return () => {
      unsubscribe();
    };
  }, [subscribe]);

  useEffect(() => {
    setIsLoading(true);
  }, [courtDateMsg]);

  const handleUploadAttachements = (items) => {
    setUploadedAttachments(items);
  };

  const createActivityLog = (item) => {
    setTimeout(() => {
      const [tenantId] = getTenantDetails();
      const data = {
        event_key: 'Read',
        event_type: 'document',
        event_subtype: 'read_document',
        message: `Viewed Document: ${item?.display_name}`,
        event_status: 'success',
        pk: item?.pk,
        sk: item?.sk,
        event_items: {
          case_id: item?.case_id || '',
          tenant_id: tenantId,
          case_name: item?.case_name || '',
          is_lead: item?.is_lead,
        },
      };
      addActivity(data);
    }, 5000);
    //Automated time entry logic
    isCaseFileManager && typeof onMountUnMount === 'function' && onMountUnMount('file-preview', 'mounted', caseDetails?.case_id);
  };

  const renderViewer = React.useCallback(
    (item) => {
      const viewerProps = {
        onLoad: () => createActivityLog(item),
      };
      switch (item.type) {
        case 'text':
          return <TextFileViewer fileUrl={item.url} {...viewerProps} />;
        case 'doc':
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <DocxViewer document={item.url} {...viewerProps} />
            </div>
          );
        case 'pdf':
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <PDFViewer document={item.url} {...viewerProps} />
            </div>
          );
        default:
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <DocumentViewer document={item.url} {...viewerProps} />
            </div>
          );
      }
    },
    [createActivityLog]
  );

  const renderLoader = (data) => {
    return (
      <div className="overlay d-flex flex-column p-1 align-items-center">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '3em' }}></i>
        <div className="mt-3">{data?.message}</div>
      </div>
    );
  };

  return (
    <Dialog
      className="my-dialog custom-dialog preview-dialog court-setting-modal"
      header={<CustomHeader onHide={handleCloseModal} />}
      visible={showCreateEventModal}
      modal
      onHide={handleCloseModal}
      closable={false}
    >
      <div className="row">
        <div className="d-flex mb-2 align-items-center mt-1">
          <span className="d-flex p-0 black-700" style={{ lineHeight: '18px', fontSize: '12px' }}>
            <i className="pi pi-info-circle me-2"></i>
            AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
          </span>
        </div>
        <div className="col-lg-8 col-md-6 col-12  court-settings-preview" style={{ maxHeight: '100%' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="modal-heading F-size16 text-bold">{`Preview of ${renderingData?.file_name}`}</div>
          </div>
          {renderViewer(renderingData)}
        </div>
        {isLoading ? (
          <div className="col-lg-4 col-md-6 col-12 mb-1 d-flex align-items-center justify-content-center" style={{ overflow: 'auto' }}>
            {renderLoader(courtDateMsg)}
          </div>
        ) : (
          <div className="col-lg-4 col-md-6 col-12 mb-1 court-settings-form">
            <CourtEventForm
              saveCreateEvent={saveCreateEvent}
              hideCreateEventModal={hideCreateEventModal}
              selectedItem={selectedItem}
              control={control}
              resetField={resetField}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              errors={errors}
              trigger={trigger}
              handleUploadAttachements={handleUploadAttachements}
              uploadedAttachments={uploadedAttachments}
              courtEventExtractedData={courtEventExtractedData}
            />
            <div className="footer-btn">
              <Button className="p-button-secondary outline mx-2" label="Cancel" onClick={handleCloseModal} />
              <Button className="p-button p-button-secondary" label="Save" onClick={handleSubmit(saveCreateEvent)} />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default CreateCourtEventModal;
